<template>
  <div class="row h-100">
    <div class="col-12 h-100 d-flex flex-column">
      <div class="d-flex justify-content-end mb-3">
        <b-button variant="primary" size="sm" :disabled="validationError" @click="saveField"> {{ 'save' | i18n }} </b-button>
      </div>
      <div class="poppins overflow-x-hidden overflow-auto pr-1 flex1 flex-auto">
        <div>
          <BlockHeader name="Configure field" class="mt-0" />
          <div class="row">
            <div class="col-lg-8 col-sm-12 col-xs-12">
              <Input v-model="field.title" form-group-id="Name" placeholder="Name" :on-change="setNewKey" :required="true" />
            </div>
            <div class="col-lg-4 col-sm-12 col-xs-12">
              <Select
                v-model="field.type"
                :options="typesOptions"
                :form-group-id="`type-${field.id}`"
                placeholder="entry_type"
                :on-change="onTypeChange"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-lg-8 col-sm-12 col-xs-12">
              <Input v-model="field.description" form-group-id="Description" placeholder="Description" />
            </div>
            <div
              v-if="!TYPES_LIST_WITHOUT_CHECKBOX.includes(field.type)"
              class="col-lg-4 col-sm-12 col-xs-12 mt-n2 pt-5 checkbox-wrapper"
            >
              <Checkbox
                v-if="field.type !== ENTRY_TYPES.FEED"
                v-model="field.required"
                form-group-id="Required"
                placeholder="Required"
              />
              <Checkbox
                v-model="field.internal"
                form-group-id="Internal"
                placeholder="Internal"
              />
              <Checkbox
                v-if="!noEditByMobile.includes(field.type)"
                v-model="field.editByMobile"
                form-group-id="Entry on mobile device"
                placeholder="Entry on mobile device"
              />
              <Checkbox
                v-if="field.type === ENTRY_TYPES.DROPDOWN"
                v-model="field.showTextInput"
                placeholder="Add text field for note"
                form-group-id="Add text field for note"
              />
              <Checkbox
                v-if="typesForAutoselect.includes(field.type)"
                v-model="field.rules.automaticallySelected"
                placeholder="Automatically selected"
                form-group-id="Automatically selected"
                :disabled="!isAutoselectAvailable"
              />
              <Checkbox
                v-if="attachingFileToEmail.includes(field.type)"
                v-model="field.isAttachFileToEmail"
                :form-group-id="`isAttachFileToEmail-${field.id}`"
                placeholder="Allow attaching the file to an email"
              />
            </div>
          </div>
          <div class="row" v-if="field.showTextInput && field.type === ENTRY_TYPES.DROPDOWN">
            <div class="col-lg-8 col-sm-12 col-xs-12">
              <Input v-model="field.textInputName" form-group-id="Text field name" placeholder="Text field name" :required="true" />
            </div>
          </div>
          <div class="row">
            <div class="col-lg-8 col-sm-11 col-xs-11">
              <Input
                v-model="field.key"
                form-group-id="Key"
                placeholder="Key"
                :disabled="keyDisabled"
                :error="!keyDisabled && isKeyUniqueError"
                :error-message="'Key should be unique' | i18n"
                :on-change="onChangeKey"
              />
            </div>
            <div v-if="keyDisabled" class="col-1 col-lg-1 col-sm-1 col-xs-1">
              <b-button
                v-b-tooltip.hover
                :title="'edit' | i18n"
                class="edit-btn close float-none"
                aria-label="Close"
                variant="light"
                @click="confirmEditKey"
              >
                <BIconPencil />
              </b-button>
              <b-button
                v-b-tooltip.hover
                :title="'Update Key' | i18n"
                class="edit-btn close float-none"
                aria-label="Close"
                variant="light"
                @click="setNewKey"
                style="margin-left: 15px"
              >
                <BIconArrowClockwise />
              </b-button>
            </div>
          </div>
          <div class="row" v-if="field.type === ENTRY_TYPES.MULTILINE_TEXT || field.type === ENTRY_TYPES.TEXT">
            <div class="col-12 col-lg-12 col-sm-12 col-xs-12">
              <Textarea v-model="field.defaultValue" form-group-id="Default text" placeholder="Default text" rows="20" />
            </div>
          </div>
          <div class="row" v-if="field.type === ENTRY_TYPES.LIST">
            <div class="col-lg-8 col-sm-12 col-xs-12">
              <Select v-model="field.listId" :options="listOptions" form-group-id="List" placeholder="List" required />
            </div>
          </div>
        </div>
        <div v-if="field.type === ENTRY_TYPES.LIST">
          <BlockHeader name="Rules" />
          <div class="row">
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Input v-model="field.rules.limit" form-group-id="Limit" placeholder="Limit" type="number" step="1" />
            </div>
          </div>
        </div>
        <div v-if="field.type === ENTRY_TYPES.BAR_CODE">
          <BlockHeader name="Rules" />
          <div class="row">
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Input v-model="field.rules.minLength" form-group-id="minLength" placeholder="Min length" type="number" step="1" />
            </div>
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Input v-model="field.rules.length" form-group-id="Length" placeholder="Max length" type="number" step="1" />
            </div>
          </div>
        </div>
        <div v-if="showSizeInPDFConfig">
          <BlockHeader name="Line width in PDF" />
          <div class="row">
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Select
                v-model="field.rules.sizeInPDF"
                :options="[
                  { value: 50, text: '50%' },
                  { value: 100, text: '100%' },
                ]"
                form-group-id="Line width"
                placeholder="Line width"
              />
            </div>
          </div>
        </div>
        <div v-if="typesForRules.includes(field.type)">
          <BlockHeader name="Rules" />
          <div class="row">
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Input v-model="field.rules.min" form-group-id="Minimum" placeholder="Minimum" type="number" step="1" />
            </div>
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Input v-model="field.rules.max" form-group-id="Maximum" placeholder="Maximum" type="number" step="1" />
            </div>
          </div>
        </div>
        <div v-if="TYPES_LIST_THAT_COMPARE_WITH_OTHER.includes(field.type)">
          <BlockHeader name="Rules for compare fields" />
          <div class="row">
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Select
                :value="field.comparisonsRules.fieldIdentity"
                :options="templates[$route.params.id].groups.flatMap(({ fields }) => 
                  fields
                    .filter(({ type, id }) => TYPES_LIST_THAT_COMPARE_WITH_OTHER.includes(type) && id !== field.id)
                    .map(({ id, key, title }) => ({ value: key || id, text: title }))
                )"
                form-group-id="compareField"
                placeholder="Compare field"
                :on-change="(fieldIdentity) => createAdditionalRules({ item: { fieldIdentity }, rulesName: 'comparisonsRules' })"
              />
            </div>
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Select
                :value="field.comparisonsRules.value"
                :options="TYPES_OF_COMPARE.map((value) => ({ value, text: $translate(value) }))"
                form-group-id="typeOfCompare"
                placeholder="Type of compare"
                :required="Boolean(field.comparisonsRules.fieldIdentity)"
                :disabled="!field.comparisonsRules.fieldIdentity"
                :on-change="(value) => createAdditionalRules({ item: { value }, rulesName: 'comparisonsRules' })"
              />
            </div>
          </div>
        </div>
        <div v-if="templates[$route.params.id]">
          <BlockHeader name="Rules if «required» depends from another field" />
          <div class="row">
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Select
                :value="field.requiredWhenRules.fieldIdentity"
                :options="templates[$route.params.id].groups.flatMap(({ fields }) => 
                  fields.flatMap(({ id, key, title }) => key === field.key || id === field.id ? [] : { value: key || id, text: title })
                )"
                form-group-id="dependField"
                placeholder="Depend field"
                :on-change="(fieldIdentity) => createAdditionalRules({ item: { fieldIdentity }, rulesName: 'requiredWhenRules' })"
              />
            </div>
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Select
                :value="field.requiredWhenRules.value"
                :options="TYPES_OF_FILLING_THE_FIELD.map((value) => ({ value, text: $translate(value) }))"
                form-group-id="fillingTheField"
                placeholder="Filling the field"
                :required="Boolean(field.requiredWhenRules.fieldIdentity)"
                :disabled="!field.requiredWhenRules.fieldIdentity"
                :on-change="(value) => createAdditionalRules({ item: { value }, rulesName: 'requiredWhenRules' })"
              />
            </div>
          </div>
        </div>
        <div v-if="typesForConfiguration.includes(field.type)">
          <BlockHeader name="Configuration" />
          <DropdownValues v-if="field.type === ENTRY_TYPES.DROPDOWN" :on-change="(e) => (field.values = e)" :values="field.values" />
          <FormulaValue
            v-if="field.type === ENTRY_TYPES.FORMULA"
            :on-change="updateFormulaValue"
            :values="{
              unitName: field.unitName,
              unitValue: field.unitValue,
              unitFieldName: field.unitFieldName,
            }"
          />
          <CheckboxesValues
            v-if="field.type === ENTRY_TYPES.MULTIPLE_CHECKBOXES || field.type === ENTRY_TYPES.RADIO_BUTTON"
            :on-change="(e) => (field.mappedValues = e)"
            :values="field.mappedValues"
          />
          <TextBlockValues v-if="field.type === ENTRY_TYPES.TEXT_BLOCK" :on-change="(e) => (field.mappedValues = e)" :values="field.mappedValues" />
          <div v-if="field.type === ENTRY_TYPES.PDF_ATTACHMENT || field.type === ENTRY_TYPES.PICTURE_IN_TEMPLATE">
            <span v-if="field.file" class="w-100 d-flex">
              <Input
                :value="field.file.name ? field.file.name : field.file"
                :view-mode="true"
                :form-group-id="`File-input-${field.id}`"
                placeholder="File"
                class="w-100"
              />
              <div class="pt-3 ml-3">
                <b-button v-b-tooltip.hover :title="'Delete file' | i18n" class="close float-none mt-4" variant="light" @click="field.file = null">
                  <span aria-hidden="true">&times;</span>
                </b-button>
              </div>
            </span>
            <FileInput
              v-else
              v-model="field.file"
              :form-group-id="`File-${field.id}`"
              placeholder="File"
              :file-extensions="fileType[field.type] || ''"
              :required="true"
            />
          </div>
        </div>
        <div>
          <BlockHeader name="Translation" />
          <div class="translate-actions">
            <b-button variant="primary" size="sm" @click="addLanguage" :disabled="!languagesOptions.length">
              + {{ 'Add name translation' | i18n }}
            </b-button>
            <b-button variant="outline-primary" size="sm" @click="generateTranslation" :disabled="!field.title || !selectedLanguages.length">
              {{ 'Fill in the fields with automatic translation' | i18n }}
            </b-button>
          </div>
          <span v-if="loaded">
            <div class="row" v-for="lng in selectedLanguages" :key="lng.id">
              <div class="col-lg-5 col-sm-5 col-xs-5">
                <Select
                  v-model="lng.language"
                  :options="
                    lng.language ? [{ text: TRANSLATE_LANGUAGE_NAMES[lng.language], value: lng.language }, ...languagesOptions] : languagesOptions
                  "
                  form-group-id="Language"
                  placeholder="Language"
                />
              </div>
              <div class="col-lg-5 col-sm-5 col-xs-5">
                <span v-if="!translated[lng.language].loaded">
                  {{ 'Translating...' | i18n }}
                </span>
                <Input
                  v-else
                  :value="translated[lng.language].name"
                  form-group-id="Translation"
                  placeholder="Translation"
                  :on-change="(e) => onInputChange(lng.language, e)"
                />
              </div>
              <div class="col-lg-2 col-sm-2 col-xs-2">
                <b-button
                  v-b-tooltip.hover
                  :title="'delete' | i18n"
                  class="close float-none mr-3"
                  aria-label="Close"
                  variant="light"
                  style="margin-top: 40px"
                  @click="() => deleteLanguage(lng.id)"
                >
                  <span aria-hidden="true">&times;</span>
                </b-button>
              </div>
            </div>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { ListMixin, NotificationMixin } from '../../../../../mixins';

import FileInput from '../../../common/fileInput';
import Input from '../../../common/input';
import Textarea from '../../../common/textarea';
import Select from '../../../common/select';
import Checkbox from '../../../common/checkbox';
import PageHeader from '../../../common/page-header';
import BlockHeader from '../../../common/block-header';

import DropdownValues from '../base/components/dropdown-values';
import CheckboxesValues from '../base/components/checkboxes-values';
import TextBlockValues from '../base/components/text-block-values';
import FormulaValue from '../base/components/formula-value';

import {
  ENTRY_TYPES,
  TRANSLATE_LANGUAGE,
  ENTRY_TYPES_DYNAMIC_SIZE_IN_PDF,
  NO_EDIT_BY_MOBILE,
  SIZE_IN_PDF,
  SIZE_IN_PDF_100,
  TYPES_LIST_WITHOUT_CHECKBOX,
  TYPES_LIST_THAT_COMPARE_WITH_OTHER,
  TYPES_OF_COMPARE,
  TYPES_OF_FILLING_THE_FIELD,
} from '../../../../../../../../constants';
import { fieldError, generateFieldKey } from '../base/common';

import api from '../../../../api/index';

import { BIconPencil, BIconArrowClockwise } from 'bootstrap-vue';

let keyTimeOut = null;

export default {
  name: 'FieldDetails',
  components: {
    FileInput,
    Input,
    PageHeader,
    Select,
    BlockHeader,
    Checkbox,
    DropdownValues,
    CheckboxesValues,
    TextBlockValues,
    BIconPencil,
    Textarea,
    FormulaValue,
    BIconArrowClockwise,
  },
  mixins: [ListMixin, NotificationMixin],
  beforeRouteLeave(to, from, next) {
    if (this.changed) {
      const answer = window.confirm(this.$translate('You have not saved your progress Are you sure you want to leave this page'));
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      return next();
    }
  },
  data: function () {
    return {
      ENTRY_TYPES: ENTRY_TYPES,
      field: {
        title: '',
        description: '',
        key: '',
        type: '',
        defaultValue: '',
        unitName: '',
        unitValue: '',
        unitFieldName: '',
        required: false,
        editByMobile: false,
        customKey: false,
        values: [],
        listId: null,
        mappedValues: [],
        rules: {
          min: null,
          max: null,
          limit: null,
          minLength: null,
          length: null,
        },
        translation: {},
        comparisonsRules: {},
        requiredWhenRules: {},
      },
      typesForValues: [ENTRY_TYPES.DROPDOWN, ENTRY_TYPES.MULTIPLE_CHECKBOXES, ENTRY_TYPES.RADIO_BUTTON],
      typesForConfiguration: [
        ENTRY_TYPES.DROPDOWN,
        ENTRY_TYPES.MULTIPLE_CHECKBOXES,
        ENTRY_TYPES.RADIO_BUTTON,
        ENTRY_TYPES.TEXT_BLOCK,
        ENTRY_TYPES.FORMULA,
        ENTRY_TYPES.PDF_ATTACHMENT,
        ENTRY_TYPES.PICTURE_IN_TEMPLATE,
      ],
      typesForAutoselect: [ENTRY_TYPES.TEXT, ENTRY_TYPES.TEXT_BLOCK, ENTRY_TYPES.INT, ENTRY_TYPES.DECIMAL, ENTRY_TYPES.BAR_CODE],
      isAutoselectAvailable: true,
      typesForRules: [ENTRY_TYPES.INT, ENTRY_TYPES.DECIMAL],
      typesOptions: Object.keys(ENTRY_TYPES).map((item) => ({
        value: ENTRY_TYPES[item],
        text: this.$translate(`ENTRY_${item}`),
      })),
      keyDisabled: true,
      keysList: [],
      listOptions: [],
      languages: [],
      selectedLanguages: [],
      TRANSLATE_LANGUAGE_NAMES: {},
      translated: {},
      loaded: false,
      changed: false,
      fileType: {
        [ENTRY_TYPES.PDF_ATTACHMENT]: 'application/pdf',
        [ENTRY_TYPES.PICTURE_IN_TEMPLATE]: 'image/*',
      },
      attachingFileToEmail: [ENTRY_TYPES.PHOTO, ENTRY_TYPES.FILE, ENTRY_TYPES.DRAWING, ENTRY_TYPES.PICTURE_IN_TEMPLATE, ENTRY_TYPES.PDF_ATTACHMENT],
      noEditByMobile: NO_EDIT_BY_MOBILE,
      TYPES_LIST_WITHOUT_CHECKBOX,
      TYPES_LIST_THAT_COMPARE_WITH_OTHER,
      TYPES_OF_COMPARE,
      TYPES_OF_FILLING_THE_FIELD,
    };
  },
  computed: {
    ...mapState({
      fieldDetails: (state) => state.template.field,
      templates: (state) => state.template.templates,
    }),
    isKeyUniqueError() {
      return this.keysList.includes(this.field.key);
    },
    showSizeInPDFConfig() {
      return ENTRY_TYPES_DYNAMIC_SIZE_IN_PDF.includes(this.field.type);
    },
    validationError() {
      return fieldError(this.field) || (!this.keyDisabled && this.isKeyUniqueError);
    },
    selectedLanguagesKeys: function () {
      return this.selectedLanguages.map((i) => i.language);
    },
    languagesOptions: function () {
      return this.languages.filter((l) => !this.selectedLanguagesKeys.includes(l.value)).map((l) => ({ value: l.value, text: l.text }));
    },
  },
  watch: {
    field: {
      handler: function (newValue) {
        if (this.loaded) {
          this.changed = true;
        }
      },
      deep: true,
    },
  },
  async created() {
    this.loaded = false;
    this.languages = Object.keys(TRANSLATE_LANGUAGE).map((key) => {
      this.TRANSLATE_LANGUAGE_NAMES[TRANSLATE_LANGUAGE[key]] = key;
      Vue.set(this.translated, TRANSLATE_LANGUAGE[key], {
        loaded: true,
        name: '',
      });
      return { text: key, value: TRANSLATE_LANGUAGE[key] };
    });
    this.selectedLanguages = [];

    const lists = await this.listService.get();
    this.listOptions = lists.data.map((l) => ({ value: l.id, text: l.name }));

    this.field = { ...this.field, ...this.fieldDetails };
    if (!this.field.comparisonsRules) {
      this.field.comparisonsRules = {};
    }
    if (!this.field.requiredWhenRules) {
      this.field.requiredWhenRules = {};
    }
    if (!this.field.rules.sizeInPDF) {
      const defaultSizeInPDF = SIZE_IN_PDF[this.field.type] === SIZE_IN_PDF_100 ? 100 : 50;
      this.field.rules.sizeInPDF = this.field.showTextInput ? 100 : defaultSizeInPDF;
    }
    this.keyDisabled = !this.field.customKey;

    const { translation = {} } = this.field;
    Object.keys(translation).map((key) => {
      this.translated[key].name = translation[key];
      this.selectedLanguages.push({ language: key, id: this.getID() });
    });

    if (!this.field.key) {
      this.setNewKey();
    }
    const keys = [];

    const template = this.templates[this.$route.params.id];
    if (template && template.groups) {
      template.groups.map((g) => {
        g.fields.map((f) => {
          if (f.id === this.field.id) {
            return;
          }
          if (f.rules && f.rules.automaticallySelected) {
            this.isAutoselectAvailable = false;
          }
          const k = f.customKey || f.key ? f.key : generateFieldKey(f.title);
          keys.push(k);
        });
      });
      this.keysList = keys;
    }
    setTimeout(() => {
      this.loaded = true;
    }, 100);
  },
  methods: {
    ...mapActions({ setTemplate: 'template/setTemplate' }),
    async translate(lng, cb) {
      const translation = await api.translate.translate({ message: this.field.title, language: lng.language });
      cb(translation);
    },
    generateTranslation() {
      for (const index in this.selectedLanguages) {
        const lng = this.selectedLanguages[index];
        try {
          this.translated[lng.language].loaded = false;
          this.translate(lng, (translation) => {
            this.translated[lng.language] = {
              loaded: true,
              name: translation.data,
            };
          });
        } catch (e) {
          this.translated[lng.language] = {
            loaded: true,
            name: '',
          };
        }
      }
    },
    async addLanguage() {
      await this.selectedLanguages.unshift({
        language: this.languagesOptions[0].value,
        translation: '',
        id: this.getID(),
      });
      document.querySelector('.poppins').scrollTop = 2000;
    },
    deleteLanguage(id) {
      this.selectedLanguages = this.selectedLanguages.filter((i) => i.id !== id);
    },
    getID() {
      return `${Math.round(Math.random() * 10000)}_${new Date().getTime()}`;
    },
    onTypeChange() {
      this.field.rules = {
        min: null,
        max: null,
        limit: null,
        minLength: null,
        length: null,
        sizeInPDF: SIZE_IN_PDF[this.field.type] === SIZE_IN_PDF_100 ? 100 : 50,
      };
      this.field.comparisonsRules = {};
      this.field.requiredWhenRules = {};
      this.field.values = [];
      this.field.listId = null;
      this.field.mappedValues = [];
    },
    onChangeKey(k) {
      if (keyTimeOut) {
        clearTimeout(keyTimeOut);
      }

      keyTimeOut = setTimeout(() => {
        this.field.key = generateFieldKey(k);
      }, 100);
    },
    updateFormulaValue({ unitName, unitValue, unitFieldName }) {
      this.field.unitName = unitName;
      this.field.unitValue = Number(unitValue);
      this.field.unitFieldName = unitFieldName;
    },
    setNewKey() {
      if (this.field.customKey) {
        return;
      }
      this.field.key = generateFieldKey(this.field.title);
    },
    saveField() {
      const isMaxRuleNonInt = this.field.rules.max && this.field.rules.max % 1 !== 0;
      const isMinRuleNonInt = this.field.rules.min && this.field.rules.min % 1 !== 0;
      const isLengthRuleNonInt = this.field.rules.length && this.field.rules.length % 1 !== 0;
      const isMinLengthRuleNonInt = this.field.rules.minLength && this.field.rules.minLength % 1 !== 0;
      if (isMaxRuleNonInt || isMinRuleNonInt || isLengthRuleNonInt || isMinLengthRuleNonInt) {
        return this.showNotification({
          type: 'error',
          message: this.$translate('Rules need to be only integer number'),
        });
      }

      const field = JSON.parse(JSON.stringify(this.field));
      field.file = this.field.file;

      const id = this.$route.params.id;
      const template = this.templates[id];
      const caseDetails = template.caseDetails;

      const translation = {};
      this.selectedLanguages.map((l) => {
        const key = l.language;
        if (this.translated[key].name) {
          translation[key] = this.translated[key].name;
        }
      });
      field.translation = translation;

      const groups = template.groups.map((g) => {
        return {
          ...g,
          fields: g.fields.map((f) => {
            if (f.id === field.id) {
              return field;
            }
            return f;
          }),
        };
      });
      this.setTemplate({ id, template: { groups, caseDetails, changed: template.changed || this.changed, offset: template.offset } });
      this.changed = false;
      this.$router.go(-1);
    },
    async confirmEditKey() {
      const displayModal = async () => {
        const h = this.$createElement;
        const messageVNode = h('div', { class: ['delete-modal-row'] }, [h('div', this.$translate('Do you want to change this data?'))]);

        return this.$bvModal.msgBoxConfirm([messageVNode], {
          centered: true,
          okTitle: this.$translate('yes'),
          cancelTitle: this.$translate('no'),
          cancelVariant: 'outline-primary',
          modalClass: 'confirm-modal',
        });
      };

      const isConfirmed = await displayModal();

      if (isConfirmed) {
        this.keyDisabled = false;
        this.field.customKey = true;
      }
    },
    onInputChange(key, val) {
      this.translated[key].name = val;
    },
    createAdditionalRules({ item, rulesName }) {
      if (item.value) {
        this.field[rulesName] = { ...this.field[rulesName], value: item.value };
      }
      if (item.fieldIdentity) {
        this.field[rulesName] = { ...this.field[rulesName], fieldIdentity: item.fieldIdentity };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.checkbox-wrapper {
  display: flex;
  flex-wrap: wrap;
}
.checkbox-wrapper fieldset {
  margin-right: 10px;
}

.main-content {
  overflow-y: visible;
  max-height: 100%;
}
.inputs-block {
  padding-left: 20px;
  padding-right: 20px;
}
::v-deep .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
::v-deep .active-status {
  background-color: rgb(37, 154, 43);
}
::v-deep .inprogress-status {
  background-color: rgb(239, 255, 15);
}
::v-deep .finished-status {
  background-color: rgb(90, 98, 107);
}

.statuses-block {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  width: 100%;
}

.status-block {
  display: flex;
  margin-left: 20px;
  align-content: center;
  justify-content: center;
  align-items: center;
}

.status-name {
  margin-left: 10px;
}

::v-deep .details-action {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: rgb(34, 129, 254);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 28px;
  margin: auto;
}

::v-deep .download-buttons {
  display: flex;
}
::v-deep .download-button {
  background: rgb(237, 248, 254);
  border: 2px solid rgb(175, 216, 255);
  border-radius: 4px;
  padding: 2px 13px;
  margin: 0px 5px;
  color: rgb(49, 141, 255);
  cursor: pointer;
}

::v-deep .zip-link {
  padding: 4.5px 13px;
  cursor: pointer;
  display: inline-block;
  outline: none;
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.5;

  text-decoration: none;
  margin-right: 25px;

  background: #edf8fe;
  border: 2px solid #afd8ff;
  border-radius: 4px;
  color: #318dff;
}

.edit-btn {
  font-size: 14px;
  margin-top: 40px;
}

.translate-actions {
  margin-bottom: 30px;
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.overflow-x-hidden {
  overflow-x: hidden !important;
}

.flex-auto {
  flex: auto;
}
</style>
